/**
 * React-query hooks for the course translation feature.
 *
 * Polling strategy: the translations list refetches every 4s ONLY while at
 * least one translation is PENDING/IN_PROGRESS — otherwise polling stops
 * entirely. Components share one query key, so the panel, the modal and any
 * badge all dedupe into a single request.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import courseTranslationService, {
  CourseTranslationSummary,
  describeUpdateResult,
} from '@/services/course-translation.service';

const QUERY_KEY = (courseId: string) => ['course-translations', courseId];

const isRunning = (t: CourseTranslationSummary) =>
  t.status === 'PENDING' || t.status === 'IN_PROGRESS';

export function useCourseTranslations(courseId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: QUERY_KEY(courseId ?? ''),
    queryFn: () => courseTranslationService.getTranslations(courseId as string),
    enabled: !!courseId && enabled,
    // Closing the lesson editor invalidates this list, and the header hides the
    // language switcher while it has no data — so it blinked out on every edit.
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const data = query.state.data as CourseTranslationSummary[] | undefined;
      return data?.some(isRunning) ? 4000 : false;
    },
  });
}

export function useStartTranslation(courseId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // language_name accompanies the code because the backend stores it as the
    // display name on the translated course — the code alone is not enough.
    mutationFn: (input: { language_code: string; language_name: string }) =>
      courseTranslationService.startTranslation(courseId as string, input),
    onSuccess: () => {
      toast.success('Translation started');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(courseId ?? '') });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to start translation';
      toast.error(message);
    },
  });
}

export function useRetryTranslation(courseId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { language_code: string; suppressToast?: boolean }) => {
      const code = typeof input === 'string' ? input : input.language_code;
      return courseTranslationService.retryTranslation(courseId as string, code);
    },
    onSuccess: (result, variables) => {
      const suppressToast = typeof variables === 'object' && variables.suppressToast;
      if (!suppressToast) {
        const message = describeUpdateResult(result);
        if (result.already_current) {
          toast(message);
        } else {
          toast.success(message);
        }
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(courseId ?? '') });
    },
    onError: (error: unknown, variables) => {
      const suppressToast = typeof variables === 'object' && variables.suppressToast;
      if (!suppressToast) {
        const message = error instanceof Error ? error.message : 'Failed to retry translation';
        toast.error(message);
      }
    },
  });
}

export function useDeleteTranslation(courseId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (language_code: string) =>
      courseTranslationService.deleteTranslation(courseId as string, language_code),
    onSuccess: () => {
      toast.success('Language version deleted');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(courseId ?? '') });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to delete translation';
      toast.error(message);
    },
  });
}
